import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';

import { ColumnType, MetaValues, TooltipValues } from '@component/collection-table';
import { BankAccount, DEFAULT_LOCALE_CONFIG, LocaleConfig } from '@state/entities';
import { DataTableRow } from '@component/data-table';
import { FeatureConfigurationType, FeatureConfigurationUserPermissions } from '@state/entity';
import { formatCurrency } from '@helpers/number';
import { ID } from '@state/base';
import { Investor } from '@state/investor';
import { OverviewCard } from '@component/overview-cards';
import { dateWithoutTime, parseDate } from '@helpers/date';
import { titleize, truncate } from '@helpers/string';

import {
  AddMembersContext,
  Details,
  Fund,
  FundBankAccount,
  FundNotification,
  Investor as FundInvestor,
  InvestorQuestionnaireData,
  InvestorQuestionnaireReview,
  InvestorQuestionnaireReviewActivityLog,
  InvestorTotals,
  LegalData,
  LegalRow,
  LegalSection,
  LegalSectionData,
  LegalStatus,
  PropertyType,
  QuestionnaireReviewActivity,
  Summary
} from './fund.model';

interface DetailFundPair {
  details: Details;
  fund: Fund;
}

interface FundProps {
  activeLegalStatusTags?: LegalStatus[];
  bankAccount?: FundBankAccount;
  addMembersContext?: AddMembersContext;
  details?: Details;
  featureConfigurationUserPermissions: Record<string, FeatureConfigurationUserPermissions>;
  fund?: Fund;
  investorQuestionnaireData?: InvestorQuestionnaireData[];
  investorQuestionnaireReview?: InvestorQuestionnaireReview;
  investorQuestionnaireReviewActivityLog?: InvestorQuestionnaireReviewActivityLog;
  investors?: FundInvestor[];
  investorsTotals?: InvestorTotals;
  legalData?: LegalData;
  legalSections?: LegalSection[];
  notifications?: FundNotification[];
  overviewInvestments?: OverviewCard[][];
  selectedLegalSection?: LegalSection;
  summary?: Summary;
}

const initialState: FundProps = {
  activeLegalStatusTags: undefined,
  addMembersContext: undefined,
  bankAccount: undefined,
  details: undefined,
  featureConfigurationUserPermissions: {},
  fund: undefined,
  investorQuestionnaireReview: undefined,
  investorQuestionnaireReviewActivityLog: undefined,
  investors: undefined,
  investorsTotals: undefined,
  legalData: undefined,
  legalSections: undefined,
  notifications: undefined,
  overviewInvestments: undefined,
  selectedLegalSection: undefined,
  summary: undefined
};

const MAX_LENGTH = 23;

let getCollectionTableColumnType = (type: PropertyType): ColumnType => {
  switch (type) {
    case PropertyType.Date:
      return ColumnType.Date;
    case PropertyType.String:
      return ColumnType.Tooltip;
    default:
      return undefined;
  }
};

let getTooltip = (value?: string): string => {
  if ((value?.length ?? 0) > MAX_LENGTH) return value;
};

export const getColumnValue = (
  key: string,
  columnType: ColumnType,
  metaKey?: string
): string | TooltipValues | MetaValues => {
  switch (columnType) {
    case ColumnType.Tooltip:
      return {
        main: (row: { [key: string]: string }) => truncate(row[key]),
        tooltip: (row: { [key: string]: string }) => getTooltip(row[key])
      };
    case ColumnType.Meta:
      return {
        main: (row: { [key: string]: string }) => row[key],
        meta: (row: { [key: string]: string }) => row[metaKey] ?? ''
      };
    default:
      return key;
  }
};

// TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
// Does the actual work for transforming legal sections, selector is below within class
let transformLegalSection = (state: FundProps): LegalSectionData => {
  let { legalData } = state;
  if (!legalData) return;

  let currencyColumnKeys: string[] = [];

  let columns = legalData?.columns.map((column) => {
    // Creating collection of currency column keys to performantly format rows when necessary
    if (column.type === PropertyType.Currency) currencyColumnKeys.push(column.cv_key);

    let type = getCollectionTableColumnType(column.type);

    return {
      def: column.cv_key,
      header: truncate(column.value),
      headerTooltip: getTooltip(column.value),
      numeric: column.type === PropertyType.Integer || column.type === PropertyType.Currency,
      type,
      value: getColumnValue(column.cv_key, type)
    };
  });

  let toLegalRow = (row: LegalRow): LegalRow => {
    // creating a copy of the row instead of mutating it to avoid side effects like breaking unit tests
    let transformedRow: LegalRow = { ...row };

    for (let key of currencyColumnKeys) {
      transformedRow[key] = formatCurrency(row[key] as string);
    }

    if (row['state_updated_at']) transformedRow['state_updated_at'] = parseDate(row['state_updated_at']);

    return transformedRow;
  };

  let dataSource = legalData?.rows.map(toLegalRow);

  return { columns, dataSource };
};

@Injectable({ providedIn: 'root' })
export class FundRepository {
  _fundStore$ = createStore({ name: 'holding' }, withProps<FundProps>(initialState));

  bankAccount$ = this._fundStore$.pipe(select(({ bankAccount }) => bankAccount));

  commentReviewFeatureConfiguration$ = this._fundStore$.pipe(
    select(({ featureConfigurationUserPermissions }) => featureConfigurationUserPermissions.comment_review)
  );

  questionnaireReviewFeatureConfiguration$ = this._fundStore$.pipe(
    select(
      ({ featureConfigurationUserPermissions }) => featureConfigurationUserPermissions.questionnaire_review
    )
  );

  commentReviewUserPermissions$ = this.commentReviewFeatureConfiguration$.pipe(
    select((commentReviewFeatureConfiguration) => commentReviewFeatureConfiguration?.user_permissions)
  );

  details$ = this._fundStore$.pipe(select(({ details }) => details));

  // Joined details+fund pair and the Details-tab table projections derived from it.
  // Declared here (not alphabetically) because field initializers run top-down and the
  // table queries below reference detailsWithFund$.
  detailsWithFund$ = combineLatest([this.details$, this._fundStore$.pipe(select(({ fund }) => fund))]).pipe(
    select(([details, fund]) => (details && fund ? { details, fund } : undefined))
  );

  accountingDetailsTable$ = this.detailsWithFund$.pipe(
    select((pair) => pair && this.buildAccountingDetails(pair))
  );

  bankDetailsTable$ = this.detailsWithFund$.pipe(select((pair) => pair && this.buildBankDetails(pair)));

  fundTermsTable$ = this.detailsWithFund$.pipe(select((pair) => pair && this.buildFundTerms(pair)));

  enableCommentReview$ = this.commentReviewFeatureConfiguration$.pipe(
    select(
      (commentReviewFeatureConfiguration) =>
        commentReviewFeatureConfiguration?.feature_configuration?.enabled || false
    )
  );

  enableQuestionnaireReview$ = this.questionnaireReviewFeatureConfiguration$.pipe(
    select(
      (questionnaireReviewFeatureConfiguration) =>
        questionnaireReviewFeatureConfiguration?.feature_configuration?.enabled || false
    )
  );

  localeConfig$ = this._fundStore$.pipe(select(({ fund }) => fund?.locale_config || DEFAULT_LOCALE_CONFIG));

  questionnaireReviewUserPermissions$ = this.questionnaireReviewFeatureConfiguration$.pipe(
    select(
      (questionnaireReviewFeatureConfiguration) => questionnaireReviewFeatureConfiguration?.user_permissions
    )
  );

  questionnaireReviewActivityLog$ = this._fundStore$.pipe(
    select(({ investorQuestionnaireReviewActivityLog }) => investorQuestionnaireReviewActivityLog)
  );

  // TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
  filteredLegalSectionSourceData$ = this._fundStore$.pipe(
    select((state) => {
      let { activeLegalStatusTags } = state;
      let legalSectionData = transformLegalSection(state);

      if (!activeLegalStatusTags?.length) return legalSectionData?.dataSource;

      return legalSectionData?.dataSource.filter((currentDataRow) =>
        activeLegalStatusTags?.includes(currentDataRow.signature_request_state)
      );
    })
  );

  fund$ = this._fundStore$.pipe(select(({ fund }) => fund));
  fundId$ = this.fund$.pipe(select((fund) => fund?.id));

  investorQuestionnaireReview$ = this._fundStore$.pipe(
    select(({ investorQuestionnaireReview }) => investorQuestionnaireReview)
  );

  investors$ = this._fundStore$.pipe(select(({ investors }) => investors));

  investorsTotals$ = this._fundStore$.pipe(select(({ investorsTotals }) => investorsTotals));

  // Combined view for the fund-lps list — emits whenever rows or totals change.
  // Both slices are populated in the same tap of getAndStoreInvestors$, so
  // subscribers see a consistent rows+totals snapshot per emission.
  investorsTable$ = this._fundStore$.pipe(
    select(({ investors, investorsTotals }) => ({ investors, totals: investorsTotals }))
  );

  lastInvestorQuestionnaireReviewActivity$ = this.investorQuestionnaireReview$.pipe(
    select(
      (investorQuestionnaireReview) =>
        investorQuestionnaireReview?.activities[investorQuestionnaireReview?.activities.length - 1]
    )
  );

  // TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
  // TODO Need to better understand the size of the legalData data set to better
  // understand if we should opt for performance or readability in this observable
  // (logic in .map has conflating concerns)
  legalSectionData$ = this._fundStore$.pipe(select(transformLegalSection));

  legalSections$ = this._fundStore$.pipe(
    select(({ legalSections }): LegalSection[] => {
      if (!legalSections || legalSections.length === 0) return;
      return legalSections;
    })
  );

  notifications$ = this._fundStore$.pipe(select(({ notifications }) => notifications));

  overviewInvestments$ = this._fundStore$.pipe(
    select(({ overviewInvestments }) => {
      if (!overviewInvestments) return;

      let fundOverviewCards: OverviewCard[];
      let prospectInvestorsOverviewCards: OverviewCard[];
      let investorsOverviewCards: OverviewCard[];

      // Note: Case when you don't have prospective investors
      if (overviewInvestments.length === 2) {
        [fundOverviewCards, investorsOverviewCards] = overviewInvestments;
      }

      // Note: Case when you have prospective investors
      if (overviewInvestments.length === 3) {
        [fundOverviewCards, prospectInvestorsOverviewCards, investorsOverviewCards] = overviewInvestments;
      }

      if (this.getFund()?.investors_locked) {
        investorsOverviewCards.unshift({
          class: 'circle-icon',
          icon: '/assets/icons/lock.svg',
          iconTooltip:
            'This fund has the investors locked to prevent accidental changes that may impact ownership percentage and capital call amounts. To unlock this fund, visit the Details tab.',
          label: null,
          value: null
        });
      }

      return [fundOverviewCards, prospectInvestorsOverviewCards, investorsOverviewCards];
    })
  );

  // TODO: Remove as part of https://www.pivotaltracker.com/story/show/188601470
  selectedLegalSection$ = this._fundStore$.pipe(select(({ selectedLegalSection }) => selectedLegalSection));

  summary$ = this._fundStore$.pipe(select(({ summary }) => summary));

  private buildAccountingDetails({ details, fund }: DetailFundPair): DataTableRow[] {
    let accountingDetails: DataTableRow[] = [];

    const accountingMethod: DataTableRow = {
      header: 'Accounting Method',
      value: details.accounting_method_humanized
    };
    const auditRequired: DataTableRow = {
      header: 'Audit Required',
      value: details.audit_required ? 'Yes' : 'No'
    };
    const complexCarry: DataTableRow = {
      header: 'Complex Carry',
      value: details.complex_carry_humanized
    };
    const reportingFrequency: DataTableRow = {
      header: 'Reporting Frequency',
      value: details.reporting_frequency_humanized
    };
    let outsideAuditor: DataTableRow;
    if (details.external_auditor) {
      outsideAuditor = {
        header: 'Outside Auditor',
        type: 'list',
        value: this.buildOutsideAuditorList(details)
      };
    } else {
      outsideAuditor = {
        header: 'Outside Auditor',
        type: 'string',
        value: 'No'
      };
    }
    const complexCarryDetails: DataTableRow = {
      header: 'Complex Carry Details',
      value: details.complex_carry_details
    };

    if (fund.is_managed) {
      accountingDetails = [...accountingDetails, accountingMethod, auditRequired];
    }
    accountingDetails = [
      ...accountingDetails,
      complexCarry,
      reportingFrequency,
      outsideAuditor,
      complexCarryDetails
    ];

    return accountingDetails;
  }

  private buildBankDetails({ details, fund }: DetailFundPair): DataTableRow[] {
    let bank_account: Partial<BankAccount> = details.entity.bank_account || {};

    let bankDetails: DataTableRow[] = [
      {
        header: 'Bank',
        type: 'preformatted',
        value: bank_account.bank_name
      },
      {
        header: 'Account Name',
        value: bank_account.account_name
      },
      {
        header: 'Account Number',
        value: bank_account.account_number
      },
      {
        header: 'Routing Number',
        value: bank_account.routing_number
      },
      {
        header: 'SWIFT Code',
        value: bank_account.swift_code
      },
      {
        header: 'Bank Address',
        type: 'preformatted',
        value: bank_account.address && bank_account.address.full_address
      },
      {
        header: 'Instructions',
        value: bank_account.wire_instructions
      },
      {
        header: 'Allow Check Payment',
        value: bank_account.allow_check_payment ? 'Enabled' : 'Disabled'
      }
    ];

    let checkPaymentDetails: DataTableRow[] = [];
    if (bank_account.allow_check_payment) {
      checkPaymentDetails = [
        {
          header: 'Check Payment Recipient',
          value: bank_account.check_payment_recipient
        },
        {
          header: 'Check Payment Address',
          value: bank_account.check_payment_address?.full_address
        },
        {
          header: 'Check Payment Memo',
          value: bank_account.check_payment_memo
        }
      ];
    }

    let bankContactDetails: DataTableRow[] = [];
    if (fund.is_managed) {
      bankContactDetails = [
        {
          header: 'Bank Contact',
          value: bank_account.bank_contact_name
        },
        {
          header: 'Contact Email',
          value: bank_account.bank_contact_email
        },
        {
          header: 'Contact Number',
          value: bank_account.bank_contact_phone
        }
      ];
    }

    return [...bankDetails, ...checkPaymentDetails, ...bankContactDetails];
  }

  private buildFundTerms({ details, fund }: DetailFundPair): DataTableRow[] {
    let { carried_interest_formatted } = details;
    let carried_interest = carried_interest_formatted === '%' ? null : carried_interest_formatted;

    let fundTerms: DataTableRow[] = [
      {
        header: 'Target',
        value: details.target_formatted
      }
    ];

    if (fund.is_managed) {
      fundTerms.push({
        header: 'Total Raised',
        value: details.total_fund_contribution_formatted
      });
    }

    fundTerms = [
      ...fundTerms,
      {
        header: 'Calculation Method',
        value: titleize(fund.calculation_method)
      },
      {
        header: 'Total Invested',
        value: fund.total_invested === 0 ? null : fund.total_invested_formatted
      },
      {
        header: 'Management Fee',
        value: this.managementFeeValue(details)
      },
      {
        header: 'Carried Interest',
        value: carried_interest
      },
      {
        header: 'Units for Ownership Accounting',
        value: details.ownership_units_enabled ? 'Enabled' : 'Disabled'
      },
      {
        header: 'Price Per Unit',
        value: details.ownership_units_enabled ? details.security_share_price_formatted : '-'
      },
      {
        header: 'Investors Locked',
        tooltip:
          'When enabled, this setting prevents investors from being added, removed, or their commitment amounts to be changed. This should be set to Yes once capital is to be called as the fund closes.',
        value: fund.investors_locked ? 'Yes' : 'No'
      }
    ];

    if (fund.is_managed) {
      fundTerms.push({
        header: 'Registered Agent',
        value: details.registered_agent
      });
    }

    fundTerms = [
      ...fundTerms,
      {
        header: 'Close Date',
        value: parseDate(details.close_on, dateWithoutTime)
      },
      {
        header: 'Sign By',
        value: details.execute_by ? parseDate(details.execute_by, dateWithoutTime) : null
      },
      {
        header: 'Capital Due Date',
        value: fund.capital_due_date ? parseDate(fund.capital_due_date, dateWithoutTime) : null
      },
      {
        header: 'Vintage Year',
        value: fund.vintage_year
      },
      {
        header: 'Minimum Investment',
        value: details.minimum_investment_formatted
      },
      {
        header: 'Expense Reserve',
        value: details.expense_reserve_formatted
      }
    ];

    return fundTerms;
  }

  private buildOutsideAuditorList(details: Details): string[] {
    return [details.auditor_name, details.auditor_email, details.auditor_phone];
  }

  private managementFeeValue(details: Details): string {
    let { management_fee_formatted, management_fee_term } = details;
    let value = management_fee_formatted === '%' ? null : management_fee_formatted;

    if (management_fee_term) value += ` (${management_fee_term})`;

    return value;
  }

  getEntityID(): ID {
    return this._fundStore$.getValue().fund?.entity_id;
  }

  getFeatureConfigurationUserPermissions(feature: FeatureConfigurationType): string[] {
    return this._fundStore$.getValue().featureConfigurationUserPermissions[feature]?.user_permissions;
  }

  getFund(): Fund {
    return this._fundStore$.getValue().fund;
  }

  getFundDetails(): Details {
    return this._fundStore$.getValue().details;
  }

  getFundId(): ID {
    return this._fundStore$.getValue().fund?.id;
  }

  getInvestorQuestionnaireData(): InvestorQuestionnaireData[] {
    return this._fundStore$.getValue().investorQuestionnaireData;
  }

  getInvestorQuestionnaireReview(): InvestorQuestionnaireReview {
    return this._fundStore$.getValue().investorQuestionnaireReview;
  }

  getLocaleConfig(): Partial<LocaleConfig> {
    return this._fundStore$.getValue().fund?.locale_config || DEFAULT_LOCALE_CONFIG;
  }

  getQuestionnaireReviewFeatureEnabled(): boolean {
    return !!this._fundStore$.getValue().featureConfigurationUserPermissions['questionnaire_review']
      ?.feature_configuration?.enabled;
  }

  getLastInvestorQuestionnaireReviewActivity(): QuestionnaireReviewActivity | undefined {
    let activities = this._fundStore$.getValue().investorQuestionnaireReview?.activities;
    return activities ? activities[activities.length - 1] : undefined;
  }

  getInvestors(): FundInvestor[] {
    return this._fundStore$.getValue().investors;
  }

  getLegalSections(): LegalSection[] {
    return this._fundStore$.getValue().legalSections;
  }

  getAddMembersContext(): AddMembersContext {
    return this._fundStore$.getValue().addMembersContext;
  }

  getNotifications(): FundNotification[] {
    return this._fundStore$.getValue().notifications;
  }

  getSelectedLegalSection(): LegalSection {
    return this._fundStore$.getValue().selectedLegalSection;
  }

  reset(): void {
    this._fundStore$.reset();
  }

  resetAddMembersContext(): void {
    this._fundStore$.update(setProp('addMembersContext', null));
  }

  setActiveLegalStatusTags(activeLegalStatusTags: LegalStatus[]): void {
    this._fundStore$.update(setProp('activeLegalStatusTags', activeLegalStatusTags));
  }

  setAddMembersContext(context: AddMembersContext): void {
    this._fundStore$.update(setProp('addMembersContext', context));
  }

  setBankAccount(bankAccount: FundBankAccount): void {
    this._fundStore$.update(setProp('bankAccount', bankAccount));
  }

  setDetails(details: Details): void {
    this._fundStore$.update(setProp('details', details));
  }

  setFeatureConfigurationUserPermissions(
    featureConfigurationUserPermissions: FeatureConfigurationUserPermissions,
    type: string
  ): void {
    this._fundStore$.update((state) => {
      let existingFeatureConfigPermissions = state?.featureConfigurationUserPermissions;

      return {
        ...state,
        featureConfigurationUserPermissions: {
          ...existingFeatureConfigPermissions,
          [type]: featureConfigurationUserPermissions
        }
      };
    });
  }

  setFund(fund: Fund): void {
    this._fundStore$.update(setProp('fund', fund));
  }

  // The per-LP Investor (from @state/investor) is loaded by a different endpoint
  // and still exposes the *_formatted fields. Map those into the new lean
  // FundInvestor shape when patching a row.
  setFundInvestorFromInvestor(investor: Investor): void {
    let {
      carry_percent_formatted: carry_percent,
      contributed_amount_formatted: contributed_amount,
      management_fee_percent_formatted: management_fee_percent,
      total_due_formatted: subscribed
    } = investor;

    this._fundStore$.update((state) => {
      let investors = state.investors?.map((fundInvestor: FundInvestor) => {
        if (investor.id !== fundInvestor.investment_id) return fundInvestor;

        return {
          ...fundInvestor,
          carry_percent,
          contributed_amount,
          management_fee_percent,
          subscribed
        };
      });

      return { ...state, investors };
    });
  }

  setInvestorQuestionnaireData(investorQuestionnaireData: InvestorQuestionnaireData[]): void {
    this._fundStore$.update(setProp('investorQuestionnaireData', investorQuestionnaireData));
  }

  setInvestorQuestionnaireReview(investorQuestionnaireReview: InvestorQuestionnaireReview): void {
    this._fundStore$.update(setProp('investorQuestionnaireReview', investorQuestionnaireReview));
  }

  setinvestorQuestionnaireReviewActivityLog(
    investorQuestionnaireReviewActivityLog: InvestorQuestionnaireReviewActivityLog
  ): void {
    this._fundStore$.update(
      setProp('investorQuestionnaireReviewActivityLog', investorQuestionnaireReviewActivityLog)
    );
  }

  setInvestors(investors: FundInvestor[]): void {
    this._fundStore$.update(setProp('investors', investors));
  }

  setInvestorsTotals(totals: InvestorTotals): void {
    this._fundStore$.update(setProp('investorsTotals', totals));
  }

  setLegalData(legalData: LegalData): void {
    this._fundStore$.update(setProp('legalData', legalData));
  }

  setLegalSections(legalSections: LegalSection[]): void {
    this._fundStore$.update(setProp('legalSections', legalSections));
  }

  setNotifications(notifications: FundNotification[]): void {
    this._fundStore$.update(setProp('notifications', notifications));
  }

  setOverviewInvestments(overviewInvestments: OverviewCard[][]): void {
    this._fundStore$.update(setProp('overviewInvestments', overviewInvestments));
  }

  setSelectedLegalSection(section: LegalSection): void {
    this._fundStore$.update(setProp('selectedLegalSection', section));
  }

  setSummary(summary: Summary): void {
    this._fundStore$.update(setProp('summary', summary));
  }

  updateInvestors(updatedInvestors: FundInvestor[]): void {
    this._fundStore$.update((state) => {
      let investors = [...state.investors];

      for (let investor of updatedInvestors) {
        let index = investors.findIndex(
          (existing) => existing.investment_id === investor.investment_id
        );
        if (index > -1) investors[index] = investor;
      }

      return { ...state, investors };
    });
  }
}
